import { TrackJS, TrackJSErrorPayload, TrackJSInstallOptions } from 'trackjs';
import { BUILD_IDENTIFIER } from '../../generated/build-info';
import { TokenRefreshError } from '../../token.interceptor';
import { TrackjsClient } from './trackjs-client';

export const TRACKJS_TOKEN = 'be9c389736bb435b945063663ea5c9b4';

/** Dashboard-registered application key (lowercase); do not copy legacy's casing. */
export const TRACKJS_APPLICATION = 'ppm-account';

export const TRACKJS_FORWARDING_DOMAIN = 'errors.prayingpelicanmissions.org';

export const TRACKJS_HOSTNAMES = {
  staging: 'account.staging.ppm.org',
  production: 'account.ppm.org',
} as const;

export const TRACKJS_DEPLOYMENTS = {
  staging: 'staging',
  production: 'production',
} as const;

export type TrackjsDeployment =
  (typeof TRACKJS_DEPLOYMENTS)[keyof typeof TRACKJS_DEPLOYMENTS];

export const TRACKJS_PLATFORMS = {
  web: 'web',
  ios: 'ios',
  android: 'android',
} as const;

export type TrackjsPlatform = (typeof TRACKJS_PLATFORMS)[keyof typeof TRACKJS_PLATFORMS];

export function toTrackjsPlatform(capacitorPlatform: string): TrackjsPlatform {
  if (capacitorPlatform === TRACKJS_PLATFORMS.ios) {
    return TRACKJS_PLATFORMS.ios;
  }

  if (capacitorPlatform === TRACKJS_PLATFORMS.android) {
    return TRACKJS_PLATFORMS.android;
  }

  return TRACKJS_PLATFORMS.web;
}

export interface TrackjsReportingContext {
  enabled: boolean;
  deployment: TrackjsDeployment | null;
  platform: TrackjsPlatform;
}

function isNativePlatform(platform: TrackjsPlatform): boolean {
  return platform === TRACKJS_PLATFORMS.ios || platform === TRACKJS_PLATFORMS.android;
}

function isLocalWebHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase();

  return (
    normalized === 'localhost' ||
    normalized.startsWith('localhost:') ||
    normalized === '127.0.0.1' ||
    normalized.startsWith('127.0.0.1:') ||
    normalized === '[::1]' ||
    normalized.startsWith('[::1]:')
  );
}

function resolveWebDeployment(hostname: string): TrackjsDeployment | null {
  const normalized = hostname.toLowerCase();

  if (normalized === TRACKJS_HOSTNAMES.staging) {
    return TRACKJS_DEPLOYMENTS.staging;
  }

  if (normalized === TRACKJS_HOSTNAMES.production) {
    return TRACKJS_DEPLOYMENTS.production;
  }

  return null;
}

export function resolveTrackjsReportingContext(
  platform: TrackjsPlatform,
  hostname: string,
): TrackjsReportingContext {
  // Capacitor serves native builds from localhost — check platform before hostname.
  if (isNativePlatform(platform)) {
    return {
      enabled: true,
      deployment: TRACKJS_DEPLOYMENTS.production,
      platform,
    };
  }

  if (isLocalWebHostname(hostname)) {
    return {
      enabled: false,
      deployment: null,
      platform: TRACKJS_PLATFORMS.web,
    };
  }

  const deployment = resolveWebDeployment(hostname);

  return {
    enabled: deployment !== null,
    deployment,
    platform: TRACKJS_PLATFORMS.web,
  };
}

function stripQueryString(url: string): string {
  const queryIndex = url.indexOf('?');
  return queryIndex === -1 ? url : url.slice(0, queryIndex);
}

export interface ApiFailureReportContext {
  requestPath: string;
  status: number;
  route: string;
}

const ROUTINE_CLIENT_ERROR_STATUSES = new Set([400, 403, 422]);

export function shouldReportApiFailure(status: number): boolean {
  if (ROUTINE_CLIENT_ERROR_STATUSES.has(status)) {
    return false;
  }

  return status === 0 || status >= 500;
}

export function buildApiFailureReportContext(
  requestUrl: string,
  status: number,
  route: string,
): ApiFailureReportContext {
  return {
    requestPath: stripQueryString(requestUrl),
    status,
    route,
  };
}

export class ApiFailureError extends Error {
  readonly requestPath: string;
  readonly status: number;
  readonly route: string;

  constructor(context: ApiFailureReportContext) {
    super(`API request failed with status ${context.status}`);
    this.name = 'ApiFailureError';
    this.requestPath = context.requestPath;
    this.status = context.status;
    this.route = context.route;
  }
}

export function reportApiFailure(
  trackjs: TrackjsClient,
  context: ApiFailureReportContext,
): void {
  if (!trackjs.isInstalled()) {
    return;
  }

  trackjs.track(new ApiFailureError(context));
}

export function scrubTrackjsPayloadQueryStrings(payload: TrackJSErrorPayload): void {
  payload.url = stripQueryString(payload.url);

  for (const entry of payload.network) {
    entry.url = stripQueryString(entry.url);
  }

  for (const entry of payload.nav) {
    entry.from = stripQueryString(entry.from);
    entry.to = stripQueryString(entry.to);
  }
}

function isTokenRefreshError(error: unknown): boolean {
  return error instanceof TokenRefreshError || (error instanceof Error && error.name === 'TokenRefreshError');
}

export function trackjsOnError(payload: TrackJSErrorPayload, error?: Error): boolean {
  if (error !== undefined && isTokenRefreshError(error)) {
    return false;
  }

  scrubTrackjsPayloadQueryStrings(payload);
  return true;
}

export function buildTrackjsInstallConfig(
  context: TrackjsReportingContext,
): TrackJSInstallOptions {
  const config: TrackJSInstallOptions = {
    token: TRACKJS_TOKEN,
    application: TRACKJS_APPLICATION,
    forwardingDomain: TRACKJS_FORWARDING_DOMAIN,
    enabled: context.enabled,
    version: BUILD_IDENTIFIER,
    network: {
      error: false,
    },
    onError: trackjsOnError,
  };

  return config;
}

export function installTrackjs(platform: TrackjsPlatform, hostname: string): void {
  const context = resolveTrackjsReportingContext(platform, hostname);
  const config = buildTrackjsInstallConfig(context);

  if (!context.enabled) {
    return;
  }

  if (!TrackJS.isInstalled()) {
    TrackJS.install(config);
  }

  if (context.deployment !== null) {
    TrackJS.addMetadata('deployment', context.deployment);
  }

  TrackJS.addMetadata('platform', context.platform);
}
